import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { injectM3Tokens } from "./style/m3Palette";
import createAppTheme from "./style/theme";

// Publish the M3 tokens as CSS variables before the first render.
injectM3Tokens();

const ColorModeContext = createContext();

export const useColorMode = () => useContext(ColorModeContext);

export default function ThemeProviderWrapper({ children }) {
  const [mode, setMode] = useState(() => {
    return localStorage.getItem("vrtta_theme_mode") || "system";
  });

  const [systemMode, setSystemMode] = useState(() =>
    window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => setSystemMode(e.matches ? "dark" : "light");
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const setThemeMode = (newMode) => {
    setMode(newMode);
    localStorage.setItem("vrtta_theme_mode", newMode);
  };

  const activeMode = mode === "system" ? systemMode : mode;

  const theme = useMemo(() => createAppTheme(activeMode), [activeMode]);

  useEffect(() => {
    document.body.setAttribute("data-theme", activeMode);
    document.documentElement.setAttribute("data-theme", activeMode);
  }, [activeMode]);

  const toggleColorMode = () => {
    const next = mode === "light" ? "dark" : "light";
    setThemeMode(next);
  };

  return (
    <ColorModeContext.Provider value={{
      mode: activeMode,
      activeMode,
      themePreference: mode,
      setThemeMode,
      toggleColorMode
    }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          data-theme={activeMode}
          sx={{
            minHeight: "100vh",
            backgroundColor: theme.palette.background.default,
            transition: "background-color 0.3s ease",
          }}>
          {children}
        </Box>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
